import React from 'react';
import SelectSocialProfiles, {
  PortfolioMarkers,
} from '../selectSocialProfiles/SelectSocialProfiles';
import Modal from 'components/shared/Modal';
import { Button } from '@kontentino/ui';
import PageTypeUtils from 'app/utils/pageType';
import { TYPE } from '@kontentino/kontentino-constants/Pages';
import { useTranslation } from 'react-i18next';
import { NewAddedPage } from 'types/Page';
import { SocialLoginPage, SocialLoginTokenType } from 'types/SocialLogin';
import { ApiClientError } from 'api/client';
import useSocialProfilesSelection from './useSocialProfilesSelection';

interface Props {
  data: SocialLoginPage[];
  onPagesCreate: (page: NewAddedPage) => void;
  isSingleSelect?: boolean;
  // Reached from a Reconnect/Refresh entry rather than a plain add. Not read
  // yet: the picker will start only the connected pages ticked.
  isReconnect?: boolean;
  // The one page a profile row's button was clicked for: only it starts ticked.
  targetExternalId?: string | null;
  isPartial?: boolean;
  // Safe Connect: the portfolios whose headers carry markers.
  portfolios?: PortfolioMarkers[];
  connectedPagesExternalIds: string[];
  page: {
    type: number;
    // Reconnect target. `addProfiles` has no page-id parameter — the backend
    // matches on `external_id` — so this is threaded to the boundary but not
    // sent. Remove it only once the API question is settled either way.
    id?: number;
  };
  onError?: (error: ApiClientError) => void;
  clientBusinessId?: string | null;
  tokenType?: SocialLoginTokenType;
  userKey?: string | null;
}

const GenericProfilesSelectForm = ({
  data,
  onPagesCreate,
  connectedPagesExternalIds,
  isSingleSelect,
  targetExternalId,
  isPartial,
  portfolios,
  page,
  onError,
  clientBusinessId,
  tokenType,
  userKey,
}: Props) => {
  const { t } = useTranslation();
  const selection = useSocialProfilesSelection({
    data,
    pageType: page.type,
    connectedPagesExternalIds,
    onPagesCreate,
    onError,
    isSingleSelect,
    clientBusinessId,
    tokenType,
    userKey,
    targetExternalId,
  });

  // A flex column under a capped modal (Form): the picker's list scrolls,
  // and the footer with Add never shrinks out of view.
  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
      <SelectSocialProfiles
        profiles={selection.profiles}
        selectedIds={selection.selectedProfiles}
        isPartial={isPartial}
        portfolios={portfolios}
        onSelect={(id) => selection.handleSelect(id as string)}
        onSelectAll={selection.handleSelectAll}
        onSelectNone={selection.handleSelectNone}
      />
      <Modal.Footer className="tw-shrink-0 tw-px-0">
        <Button
          disabled={!selection.canSubmit}
          isLoading={selection.isSubmitting}
          onClick={selection.handleSubmit}
          data-name={`posts_add-profile-modal_${PageTypeUtils.getName(
            TYPE.FACEBOOK,
          )}_confirm`}
          className="tw-w-full"
        >
          {t('addProfile')}
        </Button>
      </Modal.Footer>
    </div>
  );
};

export default GenericProfilesSelectForm;
